import { createStore } from "vuex";
import { webSocketUrl } from "../common/project.config";
import axios from "../util/axios";
import { ElMessage } from "element-plus";
import i18n from "../i18n/index";
import User from "./User";
import Store from "./Store";
import Member from "./Member";
import Pay from "./Pay";
import Upgrade from "./Upgrade";
import Photo from "./Photo";
import Wall from "./Wall";
import Chat from "./Chat";
import Message from "./Message";
import Report from "./Report";
import Notification from "./Notification";
import GlobalGift from "./GlobalGift";

export default createStore({
  state: {
    lang: localStorage.getItem("lang") || "en",
    general_authority: [],
    silvercard_authority: [],
    goldcard_authority: [],
    ws: null,
    wsReady: false,
    wsStatus: "disconnected",
    wsReconnectTimer: null,
    wsHeartbeatTimer: null,
    wsAuthTimer: null,
    wsLastReceived: 0,
    wsResumeListener: null,
  },
  mutations: {
    // 修改语言
    changeLang(state, lang) {
      state.lang = lang;
    },
    // 设置用户权限
    setAuthority(state, data) {
      state.general_authority = data.vip0;
      state.silvercard_authority = data.vip1;
      state.goldcard_authority = data.vip2;
    },

    closeWebsocket(state) {
      clearTimeout(state.wsReconnectTimer);
      clearTimeout(state.wsHeartbeatTimer);
      clearTimeout(state.wsAuthTimer);
      state.wsReconnectTimer = null;
      state.wsReady = false;
      state.wsStatus = "disconnected";
      if (state.wsResumeListener && typeof window !== "undefined") {
        window.removeEventListener("online", state.wsResumeListener);
        window.removeEventListener("pageshow", state.wsResumeListener);
        document.removeEventListener("visibilitychange", state.wsResumeListener);
        state.wsResumeListener = null;
      }
      const ws = state.ws;
      state.ws = null;
      if (ws) {
        ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
        ws.close();
      }
    },
    clearChatSession(state) {
      state.Chat.chatList = [];
      state.Chat.globalChatList = [];
      state.Chat.curUser = {};
      state.Chat.service = {};
      state.Chat.count = 0;
    },
  },
  actions: {
    async logout({ commit }) {
      try {
        const res = await axios.post_form("/member/index/logout", {});
        if (res.data.status != 200) throw new Error("Logout rejected");
      } catch (error) {
        ElMessage.error(i18n.global.t("tips.sendfail"));
        return false;
      }
      commit("logout");
      commit("closeWebsocket");
      commit("clearChatImages");
      commit("clearChatHistory");
      commit("clearChatSession");
      return true;
    },
    // 更新用户信息
    updateUserInfo({ commit, state }) {
      axios.post_form("/member/index/userinfo", {}).then((res) => {
        res = res.data;
        if (res.status == 200) {
          commit("updateUserInfo", res.data);
          let vip = state.User.vip;
          if (vip == 0) {
            commit("setUserAuthority", state.general_authority);
          } else if (vip == 1) {
            commit("setUserAuthority", state.silvercard_authority);
          } else if (vip == 2) {
            commit("setUserAuthority", state.goldcard_authority);
          }
        }
      });
    },
    // 设置用户权限
    updateUserAuthority({ commit, state }) {
      let vip = state.User.vip;
      if (vip == 0) {
        commit("setUserAuthority", state.general_authority);
      } else if (vip == 1) {
        commit("setUserAuthority", state.silvercard_authority);
      } else if (vip == 2) {
        commit("setUserAuthority", state.goldcard_authority);
      }
    },
    // 初始化websocket
    initWebsocket({ dispatch, commit, state }) {
      const token = localStorage.getItem("token") || "";
      if (!state.User.isLogin || state.User.status != 1 ||
          !/^[a-f0-9]{32}$/.test(token) || state.ws) return;
      clearTimeout(state.wsReconnectTimer);
      state.wsReconnectTimer = null;
      const ws = new WebSocket(webSocketUrl);
      state.ws = ws;
      state.wsReady = false;
      state.wsStatus = "connecting";
      state.wsLastReceived = Date.now();
      if (!state.wsResumeListener && typeof window !== "undefined") {
        state.wsResumeListener = () => {
          if (document.visibilityState === "visible") dispatch("recoverWebsocket");
        };
        window.addEventListener("online", state.wsResumeListener);
        window.addEventListener("pageshow", state.wsResumeListener);
        document.addEventListener("visibilitychange", state.wsResumeListener);
      }
      state.wsAuthTimer = setTimeout(() => {
        if (state.ws === ws && !state.wsReady) ws.close();
      }, 12000);
      ws.onopen = () => {
        if (state.ws !== ws) return;
        ws.send(
          JSON.stringify({
            opt: "100",
            hasInit: "no",
            token,
            origin: true,
          })
        );
        dispatch("watchWebsocket");
      };
      ws.onmessage = (res) => {
        if (state.ws !== ws) return;
        let data;
        try { data = JSON.parse(res.data); } catch (error) { return; }
        if (!data || typeof data !== "object") return;
        state.wsLastReceived = Date.now();
        if (data.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }
        data.opt = Number(data.opt);
        if (data.opt == 10) {
          commit("logout");
          commit("closeWebsocket");
          commit("clearChatImages");
          commit("clearChatHistory");
          commit("clearChatSession");
          return;
        }
        if (data.status == "success") {
          if (data.opt === 0) {
            clearTimeout(state.wsAuthTimer);
            state.wsReady = true;
            state.wsStatus = "connected";
          }
          dispatch("chatEvent", data);
          if (data.opt === 0) dispatch("resumeChatImages");
        } else if (data.opt === 1) {
          if (data.client_msg_id) commit("failChatImage", data);
          else ElMessage.error(i18n.global.t(data.error_key || "chat.sendFailed"));
        }
      };
      ws.onclose = () => {
        if (state.ws !== ws) return;
        state.ws = null;
        state.wsReady = false;
        state.wsStatus = "reconnecting";
        clearTimeout(state.wsHeartbeatTimer);
        clearTimeout(state.wsAuthTimer);
        dispatch("pauseChatImages");
        commit("checkpointChatHistory");
        commit("clearChatHistory");
        if (state.User.isLogin && localStorage.getItem("token") === token) {
          state.wsReconnectTimer = setTimeout(() => dispatch("initWebsocket"), 3000);
        }
      };
      ws.onerror = () => { if (state.ws === ws) ws.close(); };
    },
    watchWebsocket({ state, dispatch }) {
      clearTimeout(state.wsHeartbeatTimer);
      const ws = state.ws;
      if (!ws) return;
      state.wsHeartbeatTimer = setTimeout(() => {
        if (state.ws !== ws) return;
        if (Date.now() - state.wsLastReceived > 65000) ws.close();
        else dispatch("watchWebsocket");
      }, 15000);
    },
    recoverWebsocket({ state, commit, dispatch }) {
      if (!state.User.isLogin || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
      const ws = state.ws;
      dispatch("pauseChatImages");
      commit("checkpointChatHistory");
      commit("clearChatHistory");
      if (ws && ws.readyState === WebSocket.OPEN && Date.now() - state.wsLastReceived < 65000) {
        state.wsReady = false;
        state.wsStatus = "connecting";
        clearTimeout(state.wsAuthTimer);
        ws.send(JSON.stringify({ opt: "100", token: localStorage.getItem("token"), hasInit: "no", origin: true }));
        state.wsAuthTimer = setTimeout(() => { if (state.ws === ws && !state.wsReady) ws.close(); }, 12000);
      } else {
        commit("closeWebsocket");
        dispatch("initWebsocket");
      }
    },
    // 发送websocket消息
    wssend({ state, dispatch }, data) {
      if (state.wsReady && state.ws && state.ws.readyState === WebSocket.OPEN) {
        try {
          state.ws.send(JSON.stringify(data));
          return true;
        } catch (error) { state.ws.close(); }
      }
      if (data.opt === 1 && !data.client_msg_id) ElMessage.error(i18n.global.t("chat.connectionLost"));
      dispatch("initWebsocket");
      return false;
    },
  },
  modules: {
    User,
    Store,
    Member,
    Pay,
    Upgrade,
    Photo,
    Wall,
    Chat,
    Message,
    Report,
    Notification,
    GlobalGift,
  },
});
